import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Button,
  Card,
  Collapse,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  MobileStepper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Controller } from 'react-hook-form';
import { NavigateFunction } from 'react-router';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import RHFRadioGroupLegacy from 'src/components/hook-form/rhf-radio-group-legacy';
import { paths } from 'src/routes/paths';
import { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RHFColorPicker from 'src/components/hook-form/rhf-color-picker';
import Iconify from 'src/components/iconify';
import { RHFUploadSingleFileAccelerator } from 'src/components/hook-form/rhf-upload';
import RHFUrlMaskField from 'src/components/hook-form/rhf-url-mask-field';
import RHFGaMaskField from 'src/components/hook-form/rhf-ga-mask-field';
import RHFTextArea from 'src/components/hook-form/rhf-text-area';
import { BASE_URL } from 'src/config-global';
import { useMyDataStore } from 'src/store/me/me.store';
// import { useState } from 'react';

type Props = {
  formValues: any;
  methods: any;
  control: any;
  businessUnits?: any[];
  isEdit: boolean;
  isSubmitting: boolean;
  stautsOptions: string[];
  statusLabel: string[];
  onSubmit: VoidFunction;
  navigate: NavigateFunction;
  handleDropBannerImage: (acceptedFiles: File[]) => void;
  handleDropContentImage: (acceptedFiles: File[]) => void;
  handleDropRegisterPageBannerImage: (acceptedFiles: File[]) => void;
  handleDropRegisterPageContentImage: (acceptedFiles: File[]) => void;
  handleDropHomePageBannerImage: (acceptedFiles: File[]) => void;
};

export default function LpEventsForm({
  formValues,
  methods,
  control,
  businessUnits,
  isEdit,
  isSubmitting,
  stautsOptions,
  statusLabel,
  handleDropBannerImage,
  handleDropContentImage,
  handleDropRegisterPageBannerImage,
  handleDropRegisterPageContentImage,
  handleDropHomePageBannerImage,
  onSubmit,
  navigate,
}: Props) {

  const theme = useTheme()

  const permissions = useMyDataStore((state) => state.me?.cnAdminInfo?.permissions?.benefits)

  const exampleImages: any = {
    'main-banners': ['login', 'home', 'register', 'benefit', 'footer', 'profile'],
    'primary-buttons': [
      'header',
      'benefits-list',
      'benefits-highlight',
      'benefit-modal',
      'benefits-tabs',
    ],
    'login-page': ['page'],
    'register-page': ['page', 'mobile'],
    'home-page': ['page'],
    'benefits-page': ['title'],
  };
  const [openCustomizationMainBanners, setOpenCustomizationMainBanners] = useState(true);
  const [openCustomizationPrimaryButtons, setOpenCustomizationPrimaryButtons] = useState(true);
  const [openExample, setOpenExample] = useState<string[]>([]);
  const [openLoginPageCustomization, setOpenLoginPageCustomization] = useState(true);
  const [openRegisterPageCustomization, setOpenRegisterPageCustomization] = useState(true);
  const [openHomePageCustomization, setOpenHomePageCustomization] = useState(true);
  const [openMyBenefitsCustomization, setOpenMyBenefitsCustomization] = useState(true);
  const [image, setImage] = useState<any>({
    'main-banners': 'login',
    'primary-buttons': 'header',
    'login-page': 'page',
    'register-page': 'page',
    'home-page': 'page',
    'benefits-page': 'title',
  });
  const [step, setStep] = useState<any>({
    'main-banners': 0,
    'primary-buttons': 0,
    'register-page': 0,
  });

  const handleChangeExample = (section: string) => {
    if (openExample.includes(section)) {
      setOpenExample([...openExample.filter((f) => f !== section)]);
    } else {
      setOpenExample([...openExample, section]);
    }
  };

  const handleNext = (section: string) => {
    const sectionImages = exampleImages[section];
    setImage({ ...image, [section]: sectionImages[step[section] + 1] });
    setStep({ ...step, [section]: step[section] + 1 });
  };

  const handleBack = (section: string) => {
    const sectionImages = exampleImages[section];
    setImage({ ...image, [section]: sectionImages[step[section] - 1] });
    setStep({ ...step, [section]: step[section] - 1 });
  };

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Card>
        <Stack spacing={2} p={4}>
          <RHFTextField name="name" label="Nome" />
          <Stack direction="row" spacing={2}>
            <Controller
              name="startDate"
              control={control}
              render={({ field, fieldState }) => (
                <DatePicker
                  sx={{ flex: 1 }}
                  closeOnSelect
                  label="Data de Início"
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message,
                      inputProps: { 
                        readOnly: true,
                      },
                    },
                    day: {
                      sx: {
                        '&.MuiPickersDay-root.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: theme.palette.common.white,
                        },
                      },
                    },
                  }}
                  value={field.value}
                  onChange={(date) => {
                    if (date) {
                      const normalizedDate = new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate()
                      );
                      field.onChange(normalizedDate);
                    } else {
                      field.onChange(null);
                    }
                  }}
                />
              )}
            />
            <Controller
              name="endDate"
              control={control}
              render={({ field, fieldState }) => (
                <DatePicker
                  sx={{ flex: 1 }}
                  closeOnSelect
                  label="Data de Fim"
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message,
                      inputProps: { 
                        readOnly: true,
                      },
                    },
                    day: {
                      sx: {
                        '&.MuiPickersDay-root.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: theme.palette.common.white,
                        },
                      },
                    },
                  }}
                  value={field.value}
                  onChange={(date) => {
                    if (date) {
                      const normalizedDate = new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate()
                      );
                      field.onChange(normalizedDate);
                    } else {
                      field.onChange(null);
                    }
                  }}
                />
              )}
            />
          </Stack>
          <RHFSelect
            disabled={isEdit}
            name="businessUnitId"
            label="Unidade de negócio"
            sx={{
              width: '100%',
              borderRadius: '3px',
            }}
          >
            {businessUnits?.map((option: any, index: number) => (
              <MenuItem key={index} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </RHFSelect>
          <RHFUrlMaskField name="URLName" label="Nome da URL" />
          <Typography variant="caption" ml={2}>
            <Typography fontWeight="bold" variant="caption">
              URL Gerada:
            </Typography>{' '}
            {`${BASE_URL}${businessUnits?.find((b) => b.value === formValues.businessUnitId)?.URLName ?? ''}${formValues.URLName}`}
          </Typography>
          <RHFGaMaskField name="GAPropertyId" label="Chave do Google Analytics" disabled={isEdit} />
          <RHFTextField
            name="GAPropertySecret"
            label="Chave secreta da API do Google Analytics"
            disabled={isEdit}
            value={isEdit ? '*******' : undefined} 
            InputProps={{
              readOnly: isEdit,
            }}
          />
          <Stack spacing={2}>
            <Typography variant="h6">Customização do conteúdo:</Typography>

            <Stack direction="row" spacing={2} mt={2}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="subtitle1">Banners Principais</Typography>
                  <IconButton
                    size="small"
                    onClick={() => setOpenCustomizationMainBanners((prev) => !prev)}
                    aria-label="Expandir customização"
                  >
                    <ExpandMoreIcon
                      sx={{
                        transform: openCustomizationMainBanners ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </Stack>
                <Collapse in={openCustomizationMainBanners}>
                  <Stack direction="row" spacing={4}>
                    <RHFColorPicker
                      name="customContent.mainBanners.bgColor"
                      label="Cor de fundo dos banners principais"
                    />
                    <RHFColorPicker
                      name="customContent.mainBanners.textColor"
                      label="Cor do texto dos banners principais"
                    />
                    <RHFColorPicker
                      name="customContent.mainBanners.buttonColor"
                      label="Cor dos botões dos banners principais"
                    />
                    <RHFColorPicker
                      name="customContent.mainBanners.buttonTextColor"
                      label="Cor do texto dos botões dos banners principais"
                    />
                  </Stack>
                  <Box height={16} />
                  <Stack direction="row" alignItems="center">
                    <Typography variant="button">Ver Exemplo</Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleChangeExample('main-banners')}
                      aria-label="Expandir customização"
                    >
                      <ExpandMoreIcon
                        sx={{
                          transform: openExample.includes('main-banners')
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </IconButton>
                  </Stack>
                  <Collapse in={openExample.includes('main-banners')}>
                    <Stack alignItems="center">
                      <Box
                        component="img"
                        src={`/assets/illustrations/example-main-banners/${image['main-banners']}.png`}
                        height={520}
                        sx={{ objectFit: 'contain' }}
                      />
                      <MobileStepper
                        variant="dots"
                        steps={6}
                        position="static"
                        activeStep={step['main-banners']}
                        sx={{ maxWidth: 400, flexGrow: 1 }}
                        nextButton={
                          <Button
                            size="small"
                            onClick={() => handleNext('main-banners')}
                            disabled={step['main-banners'] === 5}
                            sx={{ ml: 1 }}
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography variant="button">Avançar</Typography>
                              <Iconify icon="ri:arrow-right-double-line" />
                            </Stack>
                          </Button>
                        }
                        backButton={
                          <Button
                            size="small"
                            onClick={() => handleBack('main-banners')}
                            disabled={step['main-banners'] === 0}
                            sx={{ mr: 1 }}
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Iconify icon="ri:arrow-left-double-line" />
                              <Typography variant="button">Voltar</Typography>
                            </Stack>
                          </Button>
                        }
                      />
                    </Stack>
                  </Collapse>
                  <Box height={16} />
                  <Divider />
                </Collapse>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={2} mt={2}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="subtitle1">Botões Primários</Typography>
                  <IconButton
                    size="small"
                    onClick={() => setOpenCustomizationPrimaryButtons((prev) => !prev)}
                    aria-label="Expandir customização"
                  >
                    <ExpandMoreIcon
                      sx={{
                        transform: openCustomizationPrimaryButtons
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </Stack>
                <Collapse in={openCustomizationPrimaryButtons}>
                  <Stack direction="row" spacing={4}>
                    <RHFColorPicker
                      label="Cor de fundo dos botões"
                      name="customContent.primaryButtons.bgColor"
                    />
                    <RHFColorPicker
                      label="Cor do texto dos botões"
                      name="customContent.primaryButtons.textColor"
                    />
                  </Stack>
                  <Box height={16} />
                  <Stack direction="row" alignItems="center">
                    <Typography variant="button">Ver Exemplo</Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleChangeExample('primary-buttons')}
                      aria-label="Expandir customização"
                    >
                      <ExpandMoreIcon
                        sx={{
                          transform: openExample.includes('primary-buttons')
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </IconButton>
                  </Stack>
                  <Collapse in={openExample.includes('primary-buttons')}>
                    <Stack alignItems="center">
                      <Box
                        component="img"
                        src={`/assets/illustrations/example-primary-buttons/${image['primary-buttons']}.png`}
                        height={520}
                        sx={{ objectFit: 'contain' }}
                      />
                      <MobileStepper
                        variant="dots"
                        steps={5}
                        position="static"
                        activeStep={step['primary-buttons']}
                        sx={{ maxWidth: 400, flexGrow: 1 }}
                        nextButton={
                          <Button
                            size="small"
                            onClick={() => handleNext('primary-buttons')}
                            disabled={step['primary-buttons'] === 4}
                            sx={{ ml: 1 }}
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography variant="button">Avançar</Typography>
                              <Iconify icon="ri:arrow-right-double-line" />
                            </Stack>
                          </Button>
                        }
                        backButton={
                          <Button
                            size="small"
                            onClick={() => handleBack('primary-buttons')}
                            disabled={step['primary-buttons'] === 0}
                            sx={{ mr: 1 }}
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Iconify icon="ri:arrow-left-double-line" />
                              <Typography variant="button">Voltar</Typography>
                            </Stack>
                          </Button>
                        }
                      />
                    </Stack>
                  </Collapse>
                  <Box height={16} />
                  <Divider />
                </Collapse>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={2} mt={2}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="subtitle1">Página de Login</Typography>
                  <IconButton
                    size="small"
                    onClick={() => setOpenLoginPageCustomization((prev) => !prev)}
                    aria-label="Expandir customização"
                  >
                    <ExpandMoreIcon
                      sx={{
                        transform: openLoginPageCustomization ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </Stack>
                <Collapse in={openLoginPageCustomization}>
                  <Stack spacing={2}>
                    <Grid container direction="row" columnSpacing={4} rowSpacing={2}>
                      <Grid item xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2">Imagem do banner:</Typography>
                          <RHFUploadSingleFileAccelerator
                            name="customContent.loginPage.bannerImage"
                            acceptAll={false}
                            onDrop={handleDropBannerImage}
                            // Ajustar os handleDrop depois
                          />
                        </Stack>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2">Imagem do conteúdo:</Typography>
                          <RHFUploadSingleFileAccelerator
                            name="customContent.loginPage.contentImage"
                            acceptAll={false}
                            onDrop={handleDropContentImage}
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                    <Box />
                    <RHFTextArea
                      name="customContent.loginPage.bannerText"
                      label="Texto do banner"
                    />
                    <Box />
                    <Typography variant="subtitle2">Textos do conteúdo:</Typography>
                    <Stack spacing={4}>
                      <RHFTextArea
                        name="customContent.loginPage.contentText.primaryText"
                        label="Texto Primário"
                      />
                      <RHFTextArea
                        name="customContent.loginPage.contentText.secondaryText"
                        label="Texto Secundário"
                      />
                    </Stack>
                    <Box />
                    <Typography variant="subtitle2">Textos dos cards:</Typography>
                    <Stack direction="row" spacing={4}>
                      <RHFTextArea
                        name="customContent.loginPage.cardsText.upperLeft"
                        label="Superior Esquerdo"
                      />
                      <RHFTextArea
                        name="customContent.loginPage.cardsText.upperRight"
                        label="Superior Direito"
                      />
                      <RHFTextArea
                        name="customContent.loginPage.cardsText.bottomLeft"
                        label="Inferior Esquerdo"
                      />
                      <RHFTextArea
                        name="customContent.loginPage.cardsText.bottomRight"
                        label="Inferior Direito"
                      />
                    </Stack>
                  </Stack>
                  <Box height={16} />
                  <Stack direction="row" alignItems="center">
                    <Typography variant="button">Ver Exemplo</Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleChangeExample('login-page')}
                      aria-label="Expandir customização"
                    >
                      <ExpandMoreIcon
                        sx={{
                          transform: openExample.includes('login-page')
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </IconButton>
                  </Stack>
                  <Collapse in={openExample.includes('login-page')}>
                    <Stack alignItems="center">
                      <Box
                        component="img"
                        src={`/assets/illustrations/example-login-page/${image['login-page']}.png`}
                        height={800}
                        width="100vw"
                        sx={{ objectFit: 'contain' }}
                      />
                    </Stack>
                  </Collapse>
                  <Box height={16} />
                  <Divider />
                </Collapse>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={2} mt={2}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="subtitle1">Página de Cadastro:</Typography>
                  <IconButton
                    size="small"
                    onClick={() => setOpenRegisterPageCustomization((prev) => !prev)}
                    aria-label="Expandir customização"
                  >
                    <ExpandMoreIcon
                      sx={{
                        transform: openRegisterPageCustomization
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </Stack>
                <Collapse in={openRegisterPageCustomization}>
                  <Stack spacing={2}>
                    <Grid container direction="row" columnSpacing={4} rowSpacing={2}>
                      <Grid item xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography>Imagem do banner:</Typography>
                          <RHFUploadSingleFileAccelerator
                            name="customContent.registerPage.bannerImage"
                            acceptAll={false}
                            onDrop={handleDropRegisterPageBannerImage}
                          />
                        </Stack>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Stack spacing={1}>
                          <Typography>Imagem do conteúdo:</Typography>
                          <RHFUploadSingleFileAccelerator
                            name="customContent.registerPage.contentImage"
                            acceptAll={false}
                            onDrop={handleDropRegisterPageContentImage}
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                    <Box />
                    <RHFTextArea
                      name="customContent.registerPage.bannerText"
                      label="Texto do banner"
                    />
                    <Box />
                    <Typography variant="subtitle2">Textos do conteúdo:</Typography>
                    <Stack direction="row" spacing={4}>
                      <RHFTextArea
                        name="customContent.registerPage.contentText.primaryText"
                        label="Texto Primário"
                      />
                      <RHFTextArea
                        name="customContent.registerPage.contentText.secondaryText"
                        label="Texto Secundário"
                      />
                    </Stack>
                    <Box />
                    <Typography variant="subtitle2">Textos dos cards:</Typography>
                    <Stack direction="row" spacing={4}>
                      <RHFTextArea
                        name="customContent.registerPage.cardsText.upperLeft"
                        label="Superior Esquerdo"
                      />
                      <RHFTextArea
                        name="customContent.registerPage.cardsText.upperRight"
                        label="Superior Direito"
                      />
                      <RHFTextArea
                        name="customContent.registerPage.cardsText.bottomLeft"
                        label="Inferior Esquerdo"
                      />
                      <RHFTextArea
                        name="customContent.registerPage.cardsText.bottomRight"
                        label="Inferior Direito"
                      />
                    </Stack>
                  </Stack>
                  <Box height={16} />
                  <Stack direction="row" alignItems="center">
                    <Typography variant="button">Ver Exemplo</Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleChangeExample('register-page')}
                      aria-label="Expandir customização"
                    >
                      <ExpandMoreIcon
                        sx={{
                          transform: openExample.includes('register-page')
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </IconButton>
                  </Stack>
                  <Collapse in={openExample.includes('register-page')}>
                    <Stack alignItems="center">
                      <Box
                        component="img"
                        src={`/assets/illustrations/example-register-page/${image['register-page']}.png`}
                        height={800}
                        width="100vw"
                        sx={{ objectFit: 'contain' }}
                      />
                      <MobileStepper
                        variant="dots"
                        steps={2}
                        position="static"
                        activeStep={step['register-page']}
                        sx={{ maxWidth: 400, flexGrow: 1 }}
                        nextButton={
                          <Button
                            size="small"
                            onClick={() => handleNext('register-page')}
                            disabled={step['register-page'] === 1}
                            sx={{ ml: 1 }}
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography variant="button">Avançar</Typography>
                              <Iconify icon="ri:arrow-right-double-line" />
                            </Stack>
                          </Button>
                        }
                        backButton={
                          <Button
                            size="small"
                            onClick={() => handleBack('register-page')}
                            disabled={step['register-page'] === 0}
                            sx={{ mr: 1 }}
                          >
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Iconify icon="ri:arrow-left-double-line" />
                              <Typography variant="button">Voltar</Typography>
                            </Stack>
                          </Button>
                        }
                      />
                    </Stack>
                  </Collapse>
                  <Box height={16} />
                  <Divider />
                </Collapse>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={2} mt={2}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="subtitle1">Home:</Typography>
                  <IconButton
                    size="small"
                    onClick={() => setOpenHomePageCustomization((prev) => !prev)}
                    aria-label="Expandir customização"
                  >
                    <ExpandMoreIcon
                      sx={{
                        transform: openHomePageCustomization ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </Stack>
                <Collapse in={openHomePageCustomization}>
                  <Stack spacing={2}>
                    <Stack spacing={1}>
                      <Typography>Imagem do banner:</Typography>
                      <RHFUploadSingleFileAccelerator
                        name="customContent.homePage.bannerImage"
                        acceptAll={false}
                        onDrop={handleDropHomePageBannerImage}
                      />
                    </Stack>
                    <RHFTextArea
                      name="customContent.homePage.bannerText.infoText"
                      label="Texto Informativo"
                    />
                    <Typography>Texto do banner:</Typography>
                    <Stack direction="row" spacing={4}>
                      <RHFColorPicker
                        name="customContent.homePage.bannerText.specialColor"
                        label="Cor de Destaque"
                      />
                      <RHFTextField
                        name="customContent.homePage.bannerText.titleText"
                        label="Título do banner"
                      />
                    </Stack>
                    <Typography>Itens do banner:</Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <RHFTextField
                        name="customContent.homePage.bannerText.items.item1.cardText"
                        label="Texto do Card 1"
                      />
                      <RHFTextField
                        name="customContent.homePage.bannerText.items.item1.text"
                        label="Texto Auxiliar do Card"
                      />
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <RHFTextField
                        name="customContent.homePage.bannerText.items.item2.cardText"
                        label="Texto do Card 2"
                      />
                      <RHFTextField
                        name="customContent.homePage.bannerText.items.item2.text"
                        label="Texto Auxiliar do Card"
                      />
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <RHFTextField
                        name="customContent.homePage.bannerText.items.item3.cardText"
                        label="Texto do Card 3"
                      />
                      <RHFTextField
                        name="customContent.homePage.bannerText.items.item3.text"
                        label="Texto Auxiliar do Card"
                      />
                    </Stack>
                    <Typography>Texto do conteúdo:</Typography>
                    <Stack direction="row" spacing={4}>
                      <RHFTextArea
                        name="customContent.homePage.contentText.primaryText"
                        label="Texto Primário"
                      />
                      <RHFTextArea
                        name="customContent.homePage.contentText.secondaryText"
                        label="Texto Secundário"
                      />
                    </Stack>
                  </Stack>
                  <Box height={16} />
                  <Stack direction="row" alignItems="center">
                    <Typography variant="button">Ver Exemplo</Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleChangeExample('home-page')}
                      aria-label="Expandir customização"
                    >
                      <ExpandMoreIcon
                        sx={{
                          transform: openExample.includes('home-page')
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </IconButton>
                  </Stack>
                  <Collapse in={openExample.includes('home-page')}>
                    <Stack alignItems="center">
                      <Box
                        component="img"
                        src={`/assets/illustrations/example-home-page/${image['home-page']}.png`}
                        height={800}
                        width="100vw"
                        sx={{ objectFit: 'contain' }}
                      />
                    </Stack>
                  </Collapse>
                  <Box height={16} />
                  <Divider />
                </Collapse>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={2} mt={2}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="subtitle1">Página de meus benefícios:</Typography>
                  <IconButton
                    size="small"
                    onClick={() => setOpenMyBenefitsCustomization((prev) => !prev)}
                    aria-label="Expandir customização"
                  >
                    <ExpandMoreIcon
                      sx={{
                        transform: openMyBenefitsCustomization ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </Stack>
                <Collapse in={openMyBenefitsCustomization}>
                  <RHFTextField name="customContent.myBenefitsPage.titleText" label="Título" />
                  <Box height={16} />
                  <Stack direction="row" alignItems="center">
                    <Typography variant="button">Ver Exemplo</Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleChangeExample('benefits-page')}
                      aria-label="Expandir customização"
                    >
                      <ExpandMoreIcon
                        sx={{
                          transform: openExample.includes('benefits-page')
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </IconButton>
                  </Stack>
                  <Collapse in={openExample.includes('benefits-page')}>
                    <Stack alignItems="center">
                      <Box
                        component="img"
                        src={`/assets/illustrations/example-benefits-page/${image['benefits-page']}.png`}
                        height={400}
                        width="100vw"
                        sx={{ objectFit: 'contain' }}
                      />
                    </Stack>
                  </Collapse>
                  <Box height={16} />
                  <Divider />
                </Collapse>
              </Stack>
            </Stack>
          </Stack>
          <Stack>
            <Typography variant="body1" fontWeight="bold">
              Status:
            </Typography>
            <RHFRadioGroupLegacy
              name="status"
              options={stautsOptions}
              getOptionLabel={statusLabel}
            />
          </Stack>
          <Stack justifyContent="flex-end" direction="row" spacing={2}>
            <Button onClick={() => navigate(paths.dashboard.lpEventsList)}>Voltar</Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={(isEdit && !permissions?.update) || (!isEdit && !permissions?.create)}
            >
              {!isEdit ? 'Cadastrar Evento LP' : 'Editar Evento LP'}
            </LoadingButton>
          </Stack>
        </Stack>
      </Card>
    </FormProvider>
  );
}
